import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Inbox, CheckCircle2, XCircle, Send, Phone, Mail, MapPin, Clock, Award, Settings, Loader2, Star } from "lucide-react";

export default function ProQuoteRequests() {
  const [dash, setDash] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyFor, setReplyFor] = useState(null);
  const [reply, setReply] = useState({ message: "", price_estimate: "" });

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/devis/pro/dashboard").then((r) => setDash(r.data)).catch(() => {}),
      api.get("/devis/pro/requests").then((r) => setItems(r.data.items || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const act = async (aid, action) => {
    const MSG = {
      accept: "Demande acceptée — coordonnées visibles.",
      refuse: "Demande refusée.",
      realized: "Projet marqué réalisé — une invitation à laisser un avis a été envoyée au particulier.",
    };
    try {
      await api.post(`/devis/pro/assignments/${aid}/${action}`);
      toast.success(MSG[action] || "Mis à jour.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const sendReply = async (aid) => {
    if (reply.message.trim().length < 5) return toast.error("Votre proposition est trop courte.");
    try {
      await api.post(`/devis/pro/assignments/${aid}/respond`, reply);
      toast.success("Proposition envoyée au particulier.");
      setReplyFor(null); setReply({ message: "", price_estimate: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const f = dash?.founder;
  const s = dash?.stats || {};
  const freeUntil = f?.free_until ? new Date(f.free_until).toLocaleDateString("fr-FR") : "";

  return (
    <div data-testid="pro-quote-requests-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-[#172A3A]">Demandes de devis</h1>
          <p className="text-sm text-slate-500">Recevez et répondez gratuitement aux demandes près de chez vous.</p>
        </div>
        <Link to="/pro/demandes-devis/profil" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-testid="pro-quote-profile-link"><Settings size={15} /> Mes métiers & zones</Link>
      </div>

      {/* Badge Fondateur */}
      <div className="rounded-2xl bg-gradient-to-r from-[#172A3A] to-[#2a4258] text-white p-5 mb-6 flex items-center gap-4" data-testid="pro-founder-badge">
        <Award size={32} className="text-[#E05638] shrink-0" />
        <div>
          <div className="font-bold">PRO FONDATEUR — 12 MOIS GRATUITS MINIMUM</div>
          <div className="text-sm text-white/80">Réception et réponse 100% gratuites{freeUntil ? ` jusqu'au ${freeUntil} minimum` : ""}. Aucun crédit, aucune commission, aucun engagement payant.</div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[["Nouvelles", s.proposed || 0], ["Acceptées", s.accepted || 0], ["Refusées", s.refused || 0], ["Réponses", s.responses || 0]].map(([k, v]) => (
          <div key={k} className="bg-white rounded-xl border border-slate-100 p-4" data-testid={`pro-quote-stat-${k}`}>
            <div className="text-2xl font-extrabold text-[#172A3A]">{v}</div><div className="text-xs text-slate-500">{k}</div>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="p-10 text-center text-slate-400"><Loader2 className="animate-spin inline" /> Chargement…</div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-100 p-10 text-center" data-testid="pro-quote-empty">
          <Inbox size={36} className="text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Aucune demande pour l'instant. Complétez vos <Link to="/pro/demandes-devis/profil" className="text-[#E05638] font-semibold">métiers et zones</Link> pour recevoir des demandes ciblées.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map(({ assignment: a, request: r, my_response: mr }) => (
            <div key={a.id} className="bg-white rounded-2xl border border-slate-100 p-5" data-testid="pro-quote-card">
              <div className="flex items-start justify-between flex-wrap gap-2">
                <div>
                  <div className="font-heading text-lg font-bold text-[#172A3A]">{r.trade_name}{r.subneed ? ` · ${r.subneed}` : ""}</div>
                  {r.requester_type && (
                    <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-slate-100 text-slate-600" data-testid="pro-quote-requester-type">
                      {r.requester_type === "pro" ? "Professionnel" : "Particulier"}
                    </span>
                  )}
                  <div className="flex items-center gap-3 text-sm text-slate-500 mt-0.5">
                    <span className="inline-flex items-center gap-1"><MapPin size={13} /> {r.city}</span>
                    {r.urgency_label && <span className="inline-flex items-center gap-1 text-amber-700"><Clock size={13} /> {r.urgency_label}</span>}
                    <span className="text-xs text-slate-400">{r.ref}</span>
                  </div>
                </div>
                <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${a.status === "accepted" ? "bg-emerald-100 text-emerald-700" : a.status === "refused" ? "bg-slate-100 text-slate-500" : a.status === "expired" ? "bg-slate-100 text-slate-400" : "bg-amber-100 text-amber-800"}`}>
                  {a.status === "accepted" ? "Acceptée" : a.status === "refused" ? "Refusée" : a.status === "expired" ? "Expirée" : "Nouvelle"}
                </span>
              </div>
              <p className="mt-3 text-sm text-slate-700 whitespace-pre-line">{r.description}</p>
              {r.budget && <div className="mt-2 text-xs text-slate-500">Budget indicatif : <strong>{r.budget}</strong></div>}
              {r.photos?.length > 0 && <div className="flex gap-2 mt-2">{r.photos.map((u, i) => <img key={i} src={u} alt="" className="w-16 h-16 rounded-lg object-cover border" />)}</div>}

              {/* Coordonnées — visibles après acceptation */}
              {r.phone ? (
                <div className="mt-3 flex flex-wrap gap-3 text-sm p-3 rounded-xl bg-emerald-50 border border-emerald-100" data-testid="pro-quote-contact">
                  <span className="font-semibold text-[#172A3A]">{r.first_name} {r.last_name}</span>
                  <a href={`tel:${r.phone}`} className="inline-flex items-center gap-1 text-emerald-700 font-semibold"><Phone size={13} /> {r.phone}</a>
                  <a href={`mailto:${r.email}`} className="inline-flex items-center gap-1 text-emerald-700"><Mail size={13} /> {r.email}</a>
                </div>
              ) : (
                <div className="mt-3 text-xs text-slate-400 italic">Coordonnées du particulier visibles après acceptation.</div>
              )}

              {/* Actions */}
              <div className="mt-4 flex flex-wrap gap-2">
                {a.status === "proposed" && (
                  <>
                    <button onClick={() => act(a.id, "accept")} data-testid="pro-quote-accept" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold text-white bg-[#E05638] hover:bg-[#c7482d]"><CheckCircle2 size={15} /> Accepter</button>
                    <button onClick={() => act(a.id, "refuse")} data-testid="pro-quote-refuse" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 border border-slate-200 hover:bg-slate-50"><XCircle size={15} /> Refuser</button>
                  </>
                )}
                {a.status === "accepted" && !mr && (
                  <button onClick={() => setReplyFor(replyFor === a.id ? null : a.id)} data-testid="pro-quote-respond-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold text-white bg-[#172A3A] hover:bg-[#0f1c28]"><Send size={14} /> Envoyer une proposition</button>
                )}
                {mr && <span className="inline-flex items-center gap-1.5 text-sm text-emerald-700 font-semibold"><CheckCircle2 size={15} /> Proposition envoyée{mr.price_estimate ? ` (${mr.price_estimate})` : ""}</span>}
                {a.status === "accepted" && !a.realized && (
                  <button onClick={() => act(a.id, "realized")} data-testid="pro-quote-realized" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700"><CheckCircle2 size={15} /> Marquer réalisé</button>
                )}
                {a.realized && (
                  <span className="inline-flex items-center gap-1.5 text-sm text-emerald-700 font-semibold" data-testid="pro-quote-realized-badge"><Star size={15} className="fill-emerald-600 text-emerald-600" /> Projet réalisé · avis demandé au client</span>
                )}
              </div>

              {replyFor === a.id && (
                <div className="mt-3 p-4 rounded-xl bg-[#F2F4F5] space-y-2" data-testid="pro-quote-respond-form">
                  <textarea value={reply.message} onChange={(e) => setReply((x) => ({ ...x, message: e.target.value }))} placeholder="Votre proposition / devis…" className="w-full h-24 p-3 rounded-lg border border-slate-200 text-sm outline-none focus:border-[#E05638]" data-testid="pro-quote-respond-message" />
                  <input value={reply.price_estimate} onChange={(e) => setReply((x) => ({ ...x, price_estimate: e.target.value }))} placeholder="Estimation de prix (facultatif)" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm outline-none focus:border-[#E05638]" data-testid="pro-quote-respond-price" />
                  <button onClick={() => sendReply(a.id)} data-testid="pro-quote-respond-send" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold text-white bg-[#E05638] hover:bg-[#c7482d]"><Send size={14} /> Envoyer</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
